import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
    ColumnDef,
    flexRender,
    getCoreRowModel,
    useReactTable,
} from '@tanstack/react-table';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import {
    CalendarIcon,
    Download,
    Eye,
    FileSpreadsheet,
    Loader2,
    MoreHorizontal,
    Plus,
    RefreshCw,
    Search,
    Trash2,
    Wifi,
    WifiOff,
    Edit,
} from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useShallow } from 'zustand/react/shallow';
import { trpc } from '~/api/trpc';
import { paymentMethodConfig, defaultPaymentMethod } from '~/lib/payment-method-config';
import Content from '../components/layout/Content';
import { toast } from 'sonner';
import { useUserStore } from '~/stores/user-store';
import { UserRole } from '~/types/server-types/UserRole';

const paymentStatusConfig: Record<string, { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline' }> = {
    COMPLETED: { label: 'Abgeschlossen', variant: 'default' },
    PENDING: { label: 'Ausstehend', variant: 'outline' },
    FAILED: { label: 'Fehlgeschlagen', variant: 'destructive' },
    REFUNDED: { label: 'Erstattet', variant: 'secondary' },
};

const paymentTypeConfig: Record<string, { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline' }> = {
    BILLING_DRAFT_PAYMENT: { label: 'Zahlung', variant: 'default' },
    BILLING_DRAFT_REFUND: { label: 'Rückzahlung', variant: 'destructive' },
};

const PAYMENT_CSV_HEADERS = [
    'Datum',
    'Zahlungs-ID',
    'Kundenname',
    'Kunden-E-Mail',
    'Auftragsnummer',
    'Typ',
    'Zahlungsmethode',
    'Betrag',
    'Externe Referenz',
    'Stripe Transaktions-ID',
    'Notiz',
];

const escapeCsvField = (value: string): string => {
    if (value.includes(';') || value.includes('"') || value.includes('\n')) {
        return `"${value.replace(/"/g, '""')}"`;
    }
    return value;
};

const formatAmountForCsv = (cents: number): string =>
    (cents / 100).toFixed(2).replace('.', ',');

const paymentTypeLabels: Record<string, string> = {
    BILLING_DRAFT_PAYMENT: 'Zahlung',
    BILLING_DRAFT_REFUND: 'Rückzahlung',
    INVOICE_PAYMENT: 'Rechnungszahlung',
};

type PaymentExportRow = {
    id: string;
    createdAtDate: string;
    customerName: string;
    customerEmail: string;
    billingDraftNumber: string;
    type: string;
    paymentMethod: string | null;
    paidAmountCents: number;
    externalReference: string;
    stripeBalanceTransactionId: string;
    note: string;
};

const generatePaymentsCsv = (rows: PaymentExportRow[]): string => {
    const header = PAYMENT_CSV_HEADERS.map(escapeCsvField).join(';');
    const dataRows = rows.map(row => {
        const dateFormatted = format(new Date(row.createdAtDate), 'dd.MM.yyyy', { locale: de });
        const typeLabel = paymentTypeLabels[row.type] || row.type;
        const methodLabel = row.paymentMethod
            ? (paymentMethodConfig[row.paymentMethod] || defaultPaymentMethod).label
            : '';
        return [
            dateFormatted,
            row.id,
            row.customerName,
            row.customerEmail,
            row.billingDraftNumber,
            typeLabel,
            methodLabel,
            formatAmountForCsv(row.paidAmountCents),
            row.externalReference,
            row.stripeBalanceTransactionId,
            row.note,
        ].map(escapeCsvField).join(';');
    });

    return '\uFEFF' + [header, ...dataRows].join('\r\n');
};

const VOUCHER_PAYMENT_CSV_HEADERS = [
    'Datum',
    'Zahlungsmethode',
    'Betrag',
    'Bezahlte Rechnung',
    'Ursprungsrechnung',
    'Ursprungsrechnung Datum',
    'Kommentar',
];

type VoucherPaymentExportRow = {
    id: string;
    createdAtDate: string;
    paymentMethod: string | null;
    paidAmountCents: number;
    paidInvoiceNumber: string;
    paidInvoiceDate: string | null;
    sourceInvoiceNumber: string;
    sourceInvoiceDate: string | null;
    comment: string;
};

const formatDateForCsv = (value: string | null): string =>
    value ? format(new Date(value), 'dd.MM.yyyy', { locale: de }) : '';

const generateVoucherPaymentsCsv = (rows: VoucherPaymentExportRow[]): string => {
    const header = VOUCHER_PAYMENT_CSV_HEADERS.map(escapeCsvField).join(';');
    const dataRows = rows.map(row => {
        const methodLabel = row.paymentMethod
            ? (paymentMethodConfig[row.paymentMethod] || defaultPaymentMethod).label
            : '';
        return [
            formatDateForCsv(row.createdAtDate),
            methodLabel,
            formatAmountForCsv(row.paidAmountCents),
            row.paidInvoiceNumber,
            row.sourceInvoiceNumber,
            formatDateForCsv(row.sourceInvoiceDate),
            row.comment,
        ].map(escapeCsvField).join(';');
    });

    return '\uFEFF' + [header, ...dataRows].join('\r\n');
};

const downloadCsv = (csv: string, filename: string) => {
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
};

type PaymentRow = {
    id: string;
    createdAt: string;
    createdAtDate: string;
    paidAmountCents: number;
    paymentMethod: string | null;
    status: string;
    type: string;
    externalReference: string | null;
    note: string | null;
    customer: {
        id: number;
        firstName: string;
        lastName: string;
        email: string | null;
    };
    billingDraft: {
        id: string;
        billingDraftNumber: string;
        store: {
            id: number;
            name: string;
        };
    } | null;
    createdByUser: {
        id: number;
        firstName: string;
        lastName: string;
    } | null;
};

type ReaderRow = {
    id: string;
    label: string;
    serialNumber: string | null;
    deviceType: string;
    isOnline: boolean;
    lastSeenAt: string | null;
    store: {
        id: number;
        name: string;
    };
};

const Payments = () => {
    const navigate = useNavigate();
    const { role } = useUserStore(
        useShallow((state) => ({ role: state.user?.role }))
    );
    const isAdmin = role === UserRole.ADMIN;

    const [activeTab, setActiveTab] = useState('payments');
    
    // Payment filters
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedStoreId, setSelectedStoreId] = useState<string>(isAdmin ? 'all' : '');
    const [selectedMethod, setSelectedMethod] = useState<string>('all');
    const [selectedType, setSelectedType] = useState<string>('all');
    const [selectedStatus, setSelectedStatus] = useState<string>('all');
    const [page, setPage] = useState(0);
    const pageSize = 20;

    // Reader management
    const [registerDialogOpen, setRegisterDialogOpen] = useState(false);
    const [newReaderCode, setNewReaderCode] = useState('');
    const [newReaderLabel, setNewReaderLabel] = useState('');
    const [newReaderStoreId, setNewReaderStoreId] = useState<string>('');

    // CSV export
    const [isExporting, setIsExporting] = useState(false);
    const [exportFrom, setExportFrom] = useState<Date | undefined>(undefined);
    const [exportTo, setExportTo] = useState<Date | undefined>(undefined);
    const [exportPopoverOpen, setExportPopoverOpen] = useState(false);

    // Voucher/bundle CSV export
    const [isExportingVouchers, setIsExportingVouchers] = useState(false);
    const [exportVouchersFrom, setExportVouchersFrom] = useState<Date | undefined>(undefined);
    const [exportVouchersTo, setExportVouchersTo] = useState<Date | undefined>(undefined);
    const [exportVouchersPopoverOpen, setExportVouchersPopoverOpen] = useState(false);
    const { data: storesData } = trpc.stores.getAll.useQuery({ filters: {} });
    const utils = trpc.useUtils();
    const stores = useMemo(() => {
        const data = storesData?.data || [];
        return [...data].sort((a, b) => a.name.localeCompare(b.name));
    }, [storesData?.data]);

    useEffect(() => {
        if (isAdmin && selectedStoreId === '') {
            setSelectedStoreId('all');
        } else if (!isAdmin && stores.length > 0 && !selectedStoreId) {
            setSelectedStoreId(String(stores[0].id));
        }
    }, [stores, isAdmin, selectedStoreId]);

    // Fetch payments
    const { data: paymentsData, isLoading: isLoadingPayments, refetch: refetchPayments } = trpc.stripeTerminalAdmin.getAllPayments.useQuery({
        page,
        pageSize,
        storeId: selectedStoreId !== 'all' ? parseInt(selectedStoreId) : undefined,
        paymentMethod: selectedMethod !== 'all' ? selectedMethod : undefined,
        paymentType: selectedType !== 'all' ? selectedType : undefined,
        status: selectedStatus !== 'all' ? selectedStatus : undefined,
        searchTerm: searchTerm || undefined,
    }, {
        enabled: selectedStoreId !== '',
    });

    // Fetch readers
    const { data: readersData, isLoading: isLoadingReaders, refetch: refetchReaders } = trpc.stripeTerminalAdmin.getReaders.useQuery({
        page: 0,
        pageSize: 100,
    });

    // Mutations
    const registerReaderMutation = trpc.stripeTerminalAdmin.registerReader.useMutation({
        onSuccess: () => {
            toast.success('Lesegerät erfolgreich registriert');
            setRegisterDialogOpen(false);
            setNewReaderCode('');
            setNewReaderLabel('');
            setNewReaderStoreId('');
            refetchReaders();
        },
        onError: (error) => {
            toast.error(`Fehler: ${error.message}`);
        },
    });

    const deleteReaderMutation = trpc.stripeTerminalAdmin.deleteReader.useMutation({
        onSuccess: () => {
            toast.success('Lesegerät gelöscht');
            refetchReaders();
        },
        onError: (error) => {
            toast.error(`Fehler: ${error.message}`);
        },
    });

    const syncReadersMutation = trpc.stripeTerminalAdmin.syncReaderStatus.useMutation({
        onSuccess: (result) => {
            toast.success(`${result.synced} von ${result.total} Lesegeräten synchronisiert`);
            refetchReaders();
        },
        onError: (error) => {
            toast.error(`Fehler: ${error.message}`);
        },
    });

    const handleRegisterReader = () => {
        if (!newReaderCode || !newReaderLabel || !newReaderStoreId) {
            toast.error('Bitte füllen Sie alle Felder aus');
            return;
        }
        registerReaderMutation.mutate({
            registrationCode: newReaderCode,
            label: newReaderLabel,
            storeId: parseInt(newReaderStoreId),
        });
    };

    const handleDeleteReader = (readerId: string) => {
        if (confirm('Sind Sie sicher, dass Sie dieses Lesegerät löschen möchten?')) {
            deleteReaderMutation.mutate({ id: readerId });
        }
    };

    const handleExport = async () => {
        if (!exportFrom || !exportTo) {
            toast.error('Bitte wähle ein Start- und Enddatum.');
            return;
        }
        setIsExporting(true);
        try {
            const dateFrom = format(exportFrom, 'yyyy-MM-dd');
            const dateTo = format(exportTo, 'yyyy-MM-dd');
            const data = await utils.stripeTerminalAdmin.exportForCsv.fetch({ dateFrom, dateTo });
            if (data.length === 0) {
                toast.info('Keine Zahlungen im gewählten Zeitraum gefunden.');
                return;
            }
            const csv = generatePaymentsCsv(data as PaymentExportRow[]);
            const filename = `zahlungen_${dateFrom}_bis_${dateTo}.csv`;
            downloadCsv(csv, filename);
            toast.success(`${data.length} Zahlungen exportiert.`);
            setExportPopoverOpen(false);
        } catch (error) {
            toast.error('Fehler beim Export: ' + (error instanceof Error ? error.message : 'Unbekannter Fehler'));
        } finally {
            setIsExporting(false);
        }
    };

    const handleVoucherExport = async () => {
        if (!exportVouchersFrom || !exportVouchersTo) {
            toast.error('Bitte wähle ein Start- und Enddatum.');
            return;
        }
        setIsExportingVouchers(true);
        try {
            const dateFrom = format(exportVouchersFrom, 'yyyy-MM-dd');
            const dateTo = format(exportVouchersTo, 'yyyy-MM-dd');
            const data = await utils.stripeTerminalAdmin.exportVoucherPaymentsForCsv.fetch({ dateFrom, dateTo });
            if (data.length === 0) {
                toast.info('Keine Gutschein-/Bundle-Zahlungen im gewählten Zeitraum gefunden.');
                return;
            }
            const csv = generateVoucherPaymentsCsv(data as VoucherPaymentExportRow[]);
            const filename = `gutschein_bundle_zahlungen_${dateFrom}_bis_${dateTo}.csv`;
            downloadCsv(csv, filename);
            toast.success(`${data.length} Zahlungen exportiert.`);
            setExportVouchersPopoverOpen(false);
        } catch (error) {
            toast.error('Fehler beim Export: ' + (error instanceof Error ? error.message : 'Unbekannter Fehler'));
        } finally {
            setIsExportingVouchers(false);
        }
    };

    // Payment columns
    const paymentColumns: ColumnDef<PaymentRow>[] = [
        {
            accessorKey: 'createdAtDate',
            header: 'Datum',
            cell: ({ row }) => {
                const date = new Date(row.original.createdAtDate);
                return format(date, 'dd.MM.yyyy', { locale: de });
            },
        },
        {
            id: 'customer',
            header: 'Kunde',
            cell: ({ row }) => {
                const customer = row.original.customer;
                return (
                    <div 
                        className="cursor-pointer hover:underline"
                        onClick={() => navigate(`/customer/${customer.id}`)}
                    >
                        <div className="font-medium">{customer.firstName} {customer.lastName}</div>
                        {customer.email && (
                            <div className="text-sm text-muted-foreground">{customer.email}</div>
                        )}
                    </div>
                );
            },
        },
        {
            id: 'billingDraft',
            header: 'Auftrag',
            cell: ({ row }) => {
                const billingDraft = row.original.billingDraft;
                if (!billingDraft) return <span className="text-muted-foreground">-</span>;
                return (
                    <div 
                        className="cursor-pointer hover:underline font-mono text-sm"
                        onClick={() => navigate(`/billing-draft/${billingDraft.id}`)}
                    >
                        {billingDraft.billingDraftNumber}
                    </div>
                );
            },
        },
        {
            accessorKey: 'paidAmountCents',
            header: 'Betrag',
            cell: ({ row }) => {
                const amount = row.original.paidAmountCents / 100;
                const isRefund = row.original.type === 'BILLING_DRAFT_REFUND';
                return (
                    <div className={`font-medium ${isRefund ? 'text-destructive' : ''}`}>
                        {new Intl.NumberFormat('de-DE', {
                            style: 'currency',
                            currency: 'EUR',
                        }).format(amount)}
                    </div>
                );
            },
        },
        {
            accessorKey: 'type',
            header: 'Typ',
            cell: ({ row }) => {
                const typeInfo = paymentTypeConfig[row.original.type] || { label: row.original.type, variant: 'outline' as const };
                return <Badge variant={typeInfo.variant}>{typeInfo.label}</Badge>;
            },
        },
        {
            accessorKey: 'paymentMethod',
            header: 'Methode',
            cell: ({ row }) => {
                const method = row.original.paymentMethod;
                if (!method) return <span className="text-muted-foreground">-</span>;
                const config = paymentMethodConfig[method] || defaultPaymentMethod;
                const Icon = config.icon;
                return (
                    <div className="flex items-center gap-2">
                        <Icon className="h-4 w-4" />
                        <span>{config.label}</span>
                    </div>
                );
            },
        },
        {
            accessorKey: 'status',
            header: 'Status',
            cell: ({ row }) => {
                const status = paymentStatusConfig[row.original.status] || { label: row.original.status, variant: 'outline' as const };
                return <Badge variant={status.variant}>{status.label}</Badge>;
            },
        },
        {
            id: 'createdBy',
            header: 'Erstellt von',
            cell: ({ row }) => {
                const user = row.original.createdByUser;
                if (!user) return <span className="text-muted-foreground">System</span>;
                return <span>{user.firstName} {user.lastName}</span>;
            },
        },
        {
            accessorKey: 'note',
            header: 'Notiz',
            cell: ({ row }) => {
                const note = row.original.note;
                if (!note) return <span className="text-muted-foreground">-</span>;
                return <span className="text-sm max-w-[200px] truncate block" title={note}>{note}</span>;
            },
        },
        {
            id: 'actions',
            cell: ({ row }) => (
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <Button variant="ghost" className="h-8 w-8 p-0">
                            <MoreHorizontal className="h-4 w-4" />
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => navigate(`/payment/${row.original.id}`)}>
                            <Eye className="mr-2 h-4 w-4" />
                            Details anzeigen
                        </DropdownMenuItem>
                    </DropdownMenuContent>
                </DropdownMenu>
            ),
        },
    ];

    // Reader columns
    const readerColumns: ColumnDef<ReaderRow>[] = [
        {
            accessorKey: 'label',
            header: 'Bezeichnung',
            cell: ({ row }) => <span className="font-medium">{row.original.label}</span>,
        },
        {
            accessorKey: 'serialNumber',
            header: 'Seriennummer',
            cell: ({ row }) => (
                <span className="font-mono text-sm">
                    {row.original.serialNumber || '-'}
                </span>
            ),
        },
        {
            accessorKey: 'deviceType',
            header: 'Gerätetyp',
            cell: ({ row }) => {
                const type = row.original.deviceType;
                const labels: Record<string, string> = {
                    'bbpos_wisepos_e': 'WisePOS E',
                    'stripe_s700': 'Stripe S700',
                    'verifone_P400': 'Verifone P400',
                };
                return <span>{labels[type] || type}</span>;
            },
        },
        {
            id: 'store',
            header: 'Filiale',
            cell: ({ row }) => row.original.store.name,
        },
        {
            accessorKey: 'isOnline',
            header: 'Status',
            cell: ({ row }) => {
                const isOnline = row.original.isOnline;
                return (
                    <div className="flex items-center gap-2">
                        {isOnline ? (
                            <>
                                <Wifi className="h-4 w-4 text-green-600" />
                                <span className="text-green-600">Online</span>
                            </>
                        ) : (
                            <>
                                <WifiOff className="h-4 w-4 text-muted-foreground" />
                                <span className="text-muted-foreground">Offline</span>
                            </>
                        )}
                    </div>
                );
            },
        },
        {
            accessorKey: 'lastSeenAt',
            header: 'Zuletzt gesehen',
            cell: ({ row }) => {
                const lastSeen = row.original.lastSeenAt;
                if (!lastSeen) return <span className="text-muted-foreground">-</span>;
                return format(new Date(lastSeen), 'dd.MM.yyyy HH:mm', { locale: de });
            },
        },
        {
            id: 'actions',
            cell: ({ row }) => (
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <Button variant="ghost" className="h-8 w-8 p-0">
                            <MoreHorizontal className="h-4 w-4" />
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => handleDeleteReader(row.original.id)}>
                            <Trash2 className="mr-2 h-4 w-4" />
                            Löschen
                        </DropdownMenuItem>
                    </DropdownMenuContent>
                </DropdownMenu>
            ),
        },
    ];

    const paymentsTable = useReactTable({
        data: (paymentsData?.data as PaymentRow[]) || [],
        columns: paymentColumns,
        getCoreRowModel: getCoreRowModel(),
        manualPagination: true,
        pageCount: paymentsData?.pageCount || 0,
    });

    const readersTable = useReactTable({
        data: (readersData?.data as ReaderRow[]) || [],
        columns: readerColumns,
        getCoreRowModel: getCoreRowModel(),
    });

    return (
        <Content>
            <div className="container mx-auto py-10">
                <div className="flex justify-between items-center mb-6">
                    <h1 className="text-2xl font-bold">Zahlungen</h1>
                    <div className="flex gap-2">
                    <Popover open={exportPopoverOpen} onOpenChange={setExportPopoverOpen}>
                        <PopoverTrigger asChild>
                            <Button variant="outline">
                                <FileSpreadsheet className="h-4 w-4 mr-2" />
                                Export
                            </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-4" align="end">
                            <div className="space-y-4">
                                <div className="text-sm font-medium">Zeitraum wählen</div>
                                <div className="flex gap-4">
                                    <div className="space-y-2">
                                        <label className="text-xs text-muted-foreground">Von</label>
                                        <Popover>
                                            <PopoverTrigger asChild>
                                                <Button variant="outline" className="w-[160px] justify-start text-left font-normal">
                                                    <CalendarIcon className="h-4 w-4 mr-2" />
                                                    {exportFrom
                                                        ? format(exportFrom, 'dd.MM.yyyy', { locale: de })
                                                        : 'Startdatum'}
                                                </Button>
                                            </PopoverTrigger>
                                            <PopoverContent className="w-auto p-0" align="start">
                                                <Calendar
                                                    mode="single"
                                                    selected={exportFrom}
                                                    onSelect={setExportFrom}
                                                    locale={de}
                                                    disabled={(date) => exportTo ? date > exportTo : false}
                                                />
                                            </PopoverContent>
                                        </Popover>
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-xs text-muted-foreground">Bis</label>
                                        <Popover>
                                            <PopoverTrigger asChild>
                                                <Button variant="outline" className="w-[160px] justify-start text-left font-normal">
                                                    <CalendarIcon className="h-4 w-4 mr-2" />
                                                    {exportTo
                                                        ? format(exportTo, 'dd.MM.yyyy', { locale: de })
                                                        : 'Enddatum'}
                                                </Button>
                                            </PopoverTrigger>
                                            <PopoverContent className="w-auto p-0" align="start">
                                                <Calendar
                                                    mode="single"
                                                    selected={exportTo}
                                                    onSelect={setExportTo}
                                                    locale={de}
                                                    disabled={(date) => exportFrom ? date < exportFrom : false}
                                                />
                                            </PopoverContent>
                                        </Popover>
                                    </div>
                                </div>
                                <Button
                                    className="w-full"
                                    onClick={handleExport}
                                    disabled={!exportFrom || !exportTo || isExporting}
                                >
                                    {isExporting ? (
                                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                    ) : (
                                        <Download className="h-4 w-4 mr-2" />
                                    )}
                                    {isExporting ? 'Exportiere...' : 'CSV herunterladen'}
                                </Button>
                            </div>
                        </PopoverContent>
                    </Popover>
                    <Popover open={exportVouchersPopoverOpen} onOpenChange={setExportVouchersPopoverOpen}>
                        <PopoverTrigger asChild>
                            <Button variant="outline">
                                <FileSpreadsheet className="h-4 w-4 mr-2" />
                                Gutschein/Bundle Export
                            </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-4" align="end">
                            <div className="space-y-4">
                                <div className="text-sm font-medium">Zeitraum wählen</div>
                                <div className="flex gap-4">
                                    <div className="space-y-2">
                                        <label className="text-xs text-muted-foreground">Von</label>
                                        <Popover>
                                            <PopoverTrigger asChild>
                                                <Button variant="outline" className="w-[160px] justify-start text-left font-normal">
                                                    <CalendarIcon className="h-4 w-4 mr-2" />
                                                    {exportVouchersFrom
                                                        ? format(exportVouchersFrom, 'dd.MM.yyyy', { locale: de })
                                                        : 'Startdatum'}
                                                </Button>
                                            </PopoverTrigger>
                                            <PopoverContent className="w-auto p-0" align="start">
                                                <Calendar
                                                    mode="single"
                                                    selected={exportVouchersFrom}
                                                    onSelect={setExportVouchersFrom}
                                                    locale={de}
                                                    disabled={(date) => exportVouchersTo ? date > exportVouchersTo : false}
                                                />
                                            </PopoverContent>
                                        </Popover>
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-xs text-muted-foreground">Bis</label>
                                        <Popover>
                                            <PopoverTrigger asChild>
                                                <Button variant="outline" className="w-[160px] justify-start text-left font-normal">
                                                    <CalendarIcon className="h-4 w-4 mr-2" />
                                                    {exportVouchersTo
                                                        ? format(exportVouchersTo, 'dd.MM.yyyy', { locale: de })
                                                        : 'Enddatum'}
                                                </Button>
                                            </PopoverTrigger>
                                            <PopoverContent className="w-auto p-0" align="start">
                                                <Calendar
                                                    mode="single"
                                                    selected={exportVouchersTo}
                                                    onSelect={setExportVouchersTo}
                                                    locale={de}
                                                    disabled={(date) => exportVouchersFrom ? date < exportVouchersFrom : false}
                                                />
                                            </PopoverContent>
                                        </Popover>
                                    </div>
                                </div>
                                <Button
                                    className="w-full"
                                    onClick={handleVoucherExport}
                                    disabled={!exportVouchersFrom || !exportVouchersTo || isExportingVouchers}
                                >
                                    {isExportingVouchers ? (
                                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                    ) : (
                                        <Download className="h-4 w-4 mr-2" />
                                    )}
                                    {isExportingVouchers ? 'Exportiere...' : 'CSV herunterladen'}
                                </Button>
                            </div>
                        </PopoverContent>
                    </Popover>
                    </div>
                </div>

                <Tabs value={activeTab} onValueChange={setActiveTab}>
                    <TabsList className="mb-6">
                        <TabsTrigger value="payments">Zahlungshistorie</TabsTrigger>
                        <TabsTrigger value="readers">Terminal-Lesegeräte</TabsTrigger>
                    </TabsList>

                    {/* Payments Tab */}
                    <TabsContent value="payments">
                        {/* Filters */}
                        <Card className="mb-6">
                            <CardContent className="pt-6">
                                <div className="flex flex-wrap gap-4">
                                    <div className="flex-1 min-w-[200px]">
                                        <div className="relative">
                                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                            <Input
                                                placeholder="Suche nach Kunde oder Entwurfsnummer..."
                                                value={searchTerm}
                                                onChange={(e) => {
                                                    setSearchTerm(e.target.value);
                                                    setPage(0);
                                                }}
                                                className="pl-10"
                                            />
                                        </div>
                                    </div>
                                    <Select
                                        value={selectedStoreId}
                                        onValueChange={(value) => {
                                            setSelectedStoreId(value);
                                            setPage(0);
                                        }}
                                    >
                                        <SelectTrigger className="w-[180px]">
                                            <SelectValue placeholder="Alle Filialen" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {isAdmin && <SelectItem value="all">Alle Filialen</SelectItem>}
                                            {stores.map((store) => (
                                                <SelectItem key={store.id} value={String(store.id)}>
                                                    {store.name}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                    <Select
                                        value={selectedMethod}
                                        onValueChange={(value) => {
                                            setSelectedMethod(value);
                                            setPage(0);
                                        }}
                                    >
                                        <SelectTrigger className="w-[180px]">
                                            <SelectValue placeholder="Alle Methoden" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="all">Alle Methoden</SelectItem>
                                            <SelectItem value="CASH">Bar</SelectItem>
                                            <SelectItem value="CARD_TERMINAL_STRIPE">Kartenzahlung (Stripe)</SelectItem>
                                            <SelectItem value="GIFT_CARD">Wertgutschein</SelectItem>
                                            <SelectItem value="BUNDLE_VOUCHER">Bundle</SelectItem>
                                            <SelectItem value="KLARNA">Klarna</SelectItem>
                                            <SelectItem value="STRIPE_CHECKOUT">Stripe Zahlungslink</SelectItem>
                                            <SelectItem value="BANK_TRANSFER">Überweisung</SelectItem>
                                            <SelectItem value="SUMUP">SumUp</SelectItem>
                                            <SelectItem value="PAYPAL">PayPal</SelectItem>
                                            <SelectItem value="GHL_MANUAL_PAYMENT">Sonstige</SelectItem>
                                        </SelectContent>
                                    </Select>
                                    <Select
                                        value={selectedType}
                                        onValueChange={(value) => {
                                            setSelectedType(value);
                                            setPage(0);
                                        }}
                                    >
                                        <SelectTrigger className="w-[180px]">
                                            <SelectValue placeholder="Alle Typen" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="all">Alle Typen</SelectItem>
                                            <SelectItem value="BILLING_DRAFT_PAYMENT">Zahlung</SelectItem>
                                            <SelectItem value="BILLING_DRAFT_REFUND">Rückzahlung</SelectItem>
                                        </SelectContent>
                                    </Select>
                                    <Select
                                        value={selectedStatus}
                                        onValueChange={(value) => {
                                            setSelectedStatus(value);
                                            setPage(0);
                                        }}
                                    >
                                        <SelectTrigger className="w-[180px]">
                                            <SelectValue placeholder="Alle Status" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="all">Alle Status</SelectItem>
                                            <SelectItem value="COMPLETED">Abgeschlossen</SelectItem>
                                            <SelectItem value="PENDING">Ausstehend</SelectItem>
                                            <SelectItem value="FAILED">Fehlgeschlagen</SelectItem>
                                            <SelectItem value="REFUNDED">Erstattet</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                            </CardContent>
                        </Card>

                        {/* Payments Table */}
                        <Card>
                            <Table>
                                <TableHeader>
                                    {paymentsTable.getHeaderGroups().map((headerGroup) => (
                                        <TableRow key={headerGroup.id}>
                                            {headerGroup.headers.map((header) => (
                                                <TableHead key={header.id}>
                                                    {header.isPlaceholder
                                                        ? null
                                                        : flexRender(
                                                            header.column.columnDef.header,
                                                            header.getContext()
                                                        )}
                                                </TableHead>
                                            ))}
                                        </TableRow>
                                    ))}
                                </TableHeader>
                                <TableBody>
                                    {isLoadingPayments ? (
                                        Array.from({ length: 5 }).map((_, i) => (
                                            <TableRow key={i}>
                                                {paymentColumns.map((_, j) => (
                                                    <TableCell key={j}>
                                                        <Skeleton className="h-4 w-full" />
                                                    </TableCell>
                                                ))}
                                            </TableRow>
                                        ))
                                    ) : paymentsTable.getRowModel().rows.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={paymentColumns.length} className="text-center py-8 text-muted-foreground">
                                                Keine Zahlungen gefunden
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        paymentsTable.getRowModel().rows.map((row) => (
                                            <TableRow key={row.id} className="cursor-pointer hover:bg-muted/50 relative">
                                                {row.getVisibleCells().map((cell, index) => (
                                                    <TableCell key={cell.id}>
                                                        {index === 0 && (
                                                            <Link
                                                                to={`/payment/${row.original.id}`}
                                                                className="absolute inset-0 z-[1]"
                                                                tabIndex={-1}
                                                            />
                                                        )}
                                                        {cell.column.id === 'actions' || cell.column.id === 'customer' || cell.column.id === 'billingDraft' ? (
                                                            <div className="relative z-[2]">
                                                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                                            </div>
                                                        ) : (
                                                            flexRender(cell.column.columnDef.cell, cell.getContext())
                                                        )}
                                                    </TableCell>
                                                ))}
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </Card>

                        {/* Pagination */}
                        {paymentsData && paymentsData.pageCount > 1 && (
                            <div className="flex items-center justify-between mt-4">
                                <div className="text-sm text-muted-foreground">
                                    Seite {page + 1} von {paymentsData.pageCount} ({paymentsData.totalCount} Einträge)
                                </div>
                                <div className="flex gap-2">
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => setPage(Math.max(0, page - 1))}
                                        disabled={page === 0}
                                    >
                                        Zurück
                                    </Button>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => setPage(Math.min(paymentsData.pageCount - 1, page + 1))}
                                        disabled={page >= paymentsData.pageCount - 1}
                                    >
                                        Weiter
                                    </Button>
                                </div>
                            </div>
                        )}
                    </TabsContent>

                    {/* Readers Tab */}
                    <TabsContent value="readers">
                        <div className="flex justify-between items-center mb-6">
                            <div className="flex gap-2">
                                <Button
                                    variant="outline"
                                    onClick={() => syncReadersMutation.mutate()}
                                    disabled={syncReadersMutation.isPending}
                                >
                                    <RefreshCw className={`mr-2 h-4 w-4 ${syncReadersMutation.isPending ? 'animate-spin' : ''}`} />
                                    Status synchronisieren
                                </Button>
                            </div>
                            <Dialog open={registerDialogOpen} onOpenChange={setRegisterDialogOpen}>
                                <DialogTrigger asChild>
                                    <Button>
                                        <Plus className="mr-2 h-4 w-4" />
                                        Lesegerät registrieren
                                    </Button>
                                </DialogTrigger>
                                <DialogContent>
                                    <DialogHeader>
                                        <DialogTitle>Neues Lesegerät registrieren</DialogTitle>
                                        <DialogDescription>
                                            Geben Sie den Registrierungscode vom Stripe Dashboard ein.
                                        </DialogDescription>
                                    </DialogHeader>
                                    <div className="grid gap-4 py-4">
                                        <div className="grid gap-2">
                                            <Label htmlFor="registrationCode">Registrierungscode</Label>
                                            <Input
                                                id="registrationCode"
                                                placeholder="z.B. simulated-wpe-..."
                                                value={newReaderCode}
                                                onChange={(e) => setNewReaderCode(e.target.value)}
                                            />
                                        </div>
                                        <div className="grid gap-2">
                                            <Label htmlFor="readerLabel">Bezeichnung</Label>
                                            <Input
                                                id="readerLabel"
                                                placeholder="z.B. Empfangstresen"
                                                value={newReaderLabel}
                                                onChange={(e) => setNewReaderLabel(e.target.value)}
                                            />
                                        </div>
                                        <div className="grid gap-2">
                                            <Label htmlFor="readerStore">Filiale</Label>
                                            <Select
                                                value={newReaderStoreId}
                                                onValueChange={setNewReaderStoreId}
                                            >
                                                <SelectTrigger>
                                                    <SelectValue placeholder="Filiale auswählen" />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {stores.map((store) => (
                                                        <SelectItem key={store.id} value={String(store.id)}>
                                                            {store.name}
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </div>
                                    </div>
                                    <DialogFooter>
                                        <Button variant="outline" onClick={() => setRegisterDialogOpen(false)}>
                                            Abbrechen
                                        </Button>
                                        <Button 
                                            onClick={handleRegisterReader}
                                            disabled={registerReaderMutation.isPending}
                                        >
                                            {registerReaderMutation.isPending ? 'Registriere...' : 'Registrieren'}
                                        </Button>
                                    </DialogFooter>
                                </DialogContent>
                            </Dialog>
                        </div>

                        {/* Readers Table */}
                        <Card>
                            <Table>
                                <TableHeader>
                                    {readersTable.getHeaderGroups().map((headerGroup) => (
                                        <TableRow key={headerGroup.id}>
                                            {headerGroup.headers.map((header) => (
                                                <TableHead key={header.id}>
                                                    {header.isPlaceholder
                                                        ? null
                                                        : flexRender(
                                                            header.column.columnDef.header,
                                                            header.getContext()
                                                        )}
                                                </TableHead>
                                            ))}
                                        </TableRow>
                                    ))}
                                </TableHeader>
                                <TableBody>
                                    {isLoadingReaders ? (
                                        Array.from({ length: 3 }).map((_, i) => (
                                            <TableRow key={i}>
                                                {readerColumns.map((_, j) => (
                                                    <TableCell key={j}>
                                                        <Skeleton className="h-4 w-full" />
                                                    </TableCell>
                                                ))}
                                            </TableRow>
                                        ))
                                    ) : readersTable.getRowModel().rows.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={readerColumns.length} className="text-center py-8 text-muted-foreground">
                                                Keine Lesegeräte registriert
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        readersTable.getRowModel().rows.map((row) => (
                                            <TableRow key={row.id}>
                                                {row.getVisibleCells().map((cell) => (
                                                    <TableCell key={cell.id}>
                                                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                                    </TableCell>
                                                ))}
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </Card>
                    </TabsContent>
                </Tabs>
            </div>
        </Content>
    );
};

export default Payments;
